import { ChangeEvent, FormEvent, useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { isAxiosError } from 'axios';

import Modal from '@mui/material/Modal';
import MenuItem from '@mui/material/MenuItem';
import Grow from '@mui/material/Grow';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import { colors, zIndex } from 'styles';
import { submitStarterPackLead } from 'client';
import { validateEmail } from 'components/ContactForm/utils';
import { Button } from 'components/Buttons';
import { useIsMobile } from 'hooks/useIsMobile';

import WebImage from 'assets/top-web.webp';

import {
  CloseButton,
  ErrorText,
  Field,
  Form,
  FullWidthRow,
  ModalContent,
  Pill,
  PillsRow,
  SuccessIcon,
  SuccessText,
  SuccessTitle,
  SuccessWrap,
  TimelineLabel,
  Title,
  WebDecoration,
} from './StarterPackModal.styles';

interface StarterPackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const INDUSTRY_OPTIONS = ['Maritime', 'Defense', 'Aeronautics', 'Other'];
const TIMELINE_OPTIONS = ['Immediately', 'In 2 weeks', 'In 3 months'];

const REQUIRED_MESSAGE = 'This field is required';
const EMAIL_MESSAGE = 'Please enter a valid email address';
const SUBMIT_ERROR_MESSAGE = 'Something went wrong. Please try again.';

const initialErrors = {
  fullName: '',
  title: '',
  companyEmail: '',
  companyName: '',
  industry: '',
  timeline: '',
};

type FieldErrors = typeof initialErrors;

interface ValidationDetail {
  field: string;
  constraint: string;
}

const isFieldKey = (field: string): field is keyof FieldErrors =>
  Object.prototype.hasOwnProperty.call(initialErrors, field);

// Maps backend validation `details` onto the matching form fields.
// Returns true when at least one field-level error was applied.
const mapBackendErrors = (
  error: unknown,
  setErrors: (updater: (prev: FieldErrors) => FieldErrors) => void,
): boolean => {
  if (!isAxiosError(error) || error.response?.status !== 400) return false;

  const data = error.response.data as { details?: ValidationDetail[] } | undefined;
  const details = data?.details;
  if (!Array.isArray(details) || details.length === 0) return false;

  const fieldErrors: Partial<FieldErrors> = {};
  details.forEach(({ field }) => {
    if (isFieldKey(field)) {
      fieldErrors[field] = field === 'companyEmail' ? EMAIL_MESSAGE : REQUIRED_MESSAGE;
    }
  });

  const keys = Object.keys(fieldErrors);
  if (keys.length === 0) return false;

  setErrors((prev) => ({ ...prev, ...fieldErrors }));
  return true;
};

const menuPaperProps = {
  sx: {
    backgroundColor: colors.mainDark,
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: '8px',
    marginTop: '4px',
    '& .MuiMenuItem-root': {
      color: colors.white,
      fontSize: '16px',
    },
    '& .MuiMenuItem-root:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
    },
    '& .MuiMenuItem-root.Mui-selected, & .MuiMenuItem-root.Mui-selected:hover': {
      backgroundColor: colors.blue,
    },
  },
};

export const StarterPackModal = ({ isOpen, onClose }: StarterPackModalProps) => {
  const location = useLocation();
  const isMobile = useIsMobile();

  const [fullName, setFullName] = useState('');
  const [title, setTitle] = useState('');
  const [companyEmail, setCompanyEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [industry, setIndustry] = useState('');
  const [timeline, setTimeline] = useState('');
  const [message, setMessage] = useState('');

  const [errors, setErrors] = useState<FieldErrors>(initialErrors);
  const [submitError, setSubmitError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const pageName = location.pathname === '/' ? 'homepage' : location.pathname.substring(1);
  const pageTag = `${pageName}-starter-pack-application`;

  const segments = location.pathname.split('/').filter(Boolean);
  const packKey = segments[0] === 'action-packs' ? segments[1] : undefined;

  const resetForm = useCallback(() => {
    setFullName('');
    setTitle('');
    setCompanyEmail('');
    setCompanyName('');
    setIndustry('');
    setTimeline('');
    setMessage('');
    setErrors(initialErrors);
    setSubmitError('');
    setIsSubmitted(false);
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  const clearError = useCallback((key: keyof FieldErrors) => {
    setErrors((prev) => (prev[key] ? { ...prev, [key]: '' } : prev));
  }, []);

  const validate = useCallback((): boolean => {
    const next: FieldErrors = {
      fullName: fullName.trim() ? '' : REQUIRED_MESSAGE,
      title: title.trim() ? '' : REQUIRED_MESSAGE,
      companyEmail: !companyEmail.trim()
        ? REQUIRED_MESSAGE
        : validateEmail(companyEmail.trim())
        ? ''
        : EMAIL_MESSAGE,
      companyName: companyName.trim() ? '' : REQUIRED_MESSAGE,
      industry: industry ? '' : REQUIRED_MESSAGE,
      timeline: timeline ? '' : REQUIRED_MESSAGE,
    };

    setErrors(next);

    return Object.values(next).every((value) => !value);
  }, [fullName, title, companyEmail, companyName, industry, timeline]);

  const onSubmit = useCallback(
    (event: FormEvent) => {
      event.preventDefault();

      if (!validate()) return;

      setSubmitError('');
      setIsLoading(true);

      submitStarterPackLead({
        companyEmail: companyEmail.trim(),
        fullName: fullName.trim(),
        title: title.trim(),
        companyName: companyName.trim(),
        industry,
        timeline,
        message: message.trim() || undefined,
        pageTag,
        packKey,
      })
        .then(({ data }) => {
          setIsLoading(false);

          if (data?.accepted) {
            setIsSubmitted(true);
          } else {
            setSubmitError(SUBMIT_ERROR_MESSAGE);
          }
        })
        .catch((error: unknown) => {
          setIsLoading(false);

          if (!mapBackendErrors(error, setErrors)) {
            setSubmitError(SUBMIT_ERROR_MESSAGE);
          }
        });
    },
    [
      validate,
      title,
      companyName,
      industry,
      timeline,
      message,
      companyEmail,
      fullName,
      pageTag,
      packKey,
    ],
  );

  const handleChange =
    (setter: (value: string) => void, key?: keyof FieldErrors) =>
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setter(event.target.value);
      if (key) clearError(key);
    };

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      aria-labelledby="starter-pack-modal-title"
      closeAfterTransition
      sx={{
        zIndex: zIndex.modal,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      slotProps={{
        backdrop: {
          timeout: 1000,
          sx: {
            backdropFilter: 'blur(5px)',
            backgroundColor: 'rgba(0, 0, 0, 0.55)',
          },
        },
      }}
    >
      <Grow in={isOpen} timeout={1000}>
        <ModalContent>
          <WebDecoration>
            <img src={WebImage} alt="" aria-hidden="true" />
          </WebDecoration>

          <CloseButton type="button" onClick={handleClose} aria-label="Close form">
            <CloseIcon />
          </CloseButton>

          {isSubmitted ? (
            <SuccessWrap>
              <SuccessIcon>
                <CheckCircleOutlineIcon />
              </SuccessIcon>
              <SuccessTitle>Application received!</SuccessTitle>
              <SuccessText>
                Thank you for applying for the Starter Pack. Our team will review your request and
                get back to you shortly.
              </SuccessText>
              <Button type="button" onClick={handleClose} width={isMobile ? 'full' : 240}>
                Close
              </Button>
            </SuccessWrap>
          ) : (
            <>
              <Title id="starter-pack-modal-title">
                {'Fast-track Your AI Integration.\nApply for the Starter Pack.'}
              </Title>

              <Form onSubmit={onSubmit} noValidate>
            <Field
              label="Full Name"
              placeholder="Your Full Name"
              value={fullName}
              onChange={handleChange(setFullName, 'fullName')}
              error={!!errors.fullName}
              helperText={errors.fullName}
              disabled={isLoading}
            />
            <Field
              label="Title"
              value={title}
              onChange={handleChange(setTitle, 'title')}
              error={!!errors.title}
              helperText={errors.title}
              disabled={isLoading}
            />
            <Field
              label="Company email"
              value={companyEmail}
              onChange={handleChange(setCompanyEmail, 'companyEmail')}
              error={!!errors.companyEmail}
              helperText={errors.companyEmail}
              disabled={isLoading}
            />
            <Field
              label="Company Name"
              value={companyName}
              onChange={handleChange(setCompanyName, 'companyName')}
              error={!!errors.companyName}
              helperText={errors.companyName}
              disabled={isLoading}
            />
            <Field
              select
              label="Industry"
              value={industry}
              onChange={handleChange(setIndustry, 'industry')}
              error={!!errors.industry}
              helperText={errors.industry}
              disabled={isLoading}
              SelectProps={{
                MenuProps: { sx: { zIndex: zIndex.modal + 1 }, PaperProps: menuPaperProps },
              }}
            >
              {INDUSTRY_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Field>

            <FullWidthRow $disabled={isLoading}>
              <TimelineLabel>Project Start</TimelineLabel>
              <PillsRow>
                {TIMELINE_OPTIONS.map((option) => (
                  <Pill
                    key={option}
                    type="button"
                    $active={timeline === option}
                    aria-pressed={timeline === option}
                    onClick={() => {
                      setTimeline(option);
                      clearError('timeline');
                    }}
                    disabled={isLoading}
                  >
                    {option}
                  </Pill>
                ))}
              </PillsRow>
              {errors.timeline && <ErrorText>{errors.timeline}</ErrorText>}
            </FullWidthRow>

            <FullWidthRow>
              <Field
                label="How we can help you? (Optional)"
                value={message}
                onChange={handleChange(setMessage)}
                disabled={isLoading}
                multiline
                minRows={3}
              />
            </FullWidthRow>

            <FullWidthRow>
              {submitError && <ErrorText>{submitError}</ErrorText>}
              <Button
                type="submit"
                disabled={isLoading}
                width={isMobile ? 'full' : 240}
                startIcon={
                  isLoading ? <CircularProgress size={18} color="inherit" /> : undefined
                }
              >
                {isLoading ? 'Submitting…' : 'Submit Application'}
              </Button>
            </FullWidthRow>
          </Form>
            </>
          )}
        </ModalContent>
      </Grow>
    </Modal>
  );
};
